import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { ADMIN_BASE } from '../../config/adminLayers';

const LABELS: Record<string, string> = {
  workspaces: 'Workspaces',
  users: 'Users',
  dashboard: 'Dashboard',
  invoices: 'Invoices',
  'tds-followup': 'TDS / 26AS',
  subscriptions: 'Subscriptions',
  pricing: 'Pricing',
  'phone-numbers': 'Phone numbers',
  'calling-providers': 'Calling providers',
  'api-keys': 'API keys',
  workflows: 'Workflows',
  'workflow-health': 'Workflow health',
  integrations: 'Integrations',
  settings: 'Settings',
  'invoice-templates': 'Invoice templates',
  'notification-senders': 'Notification senders',
  firecrawl: 'Firecrawl',
  searxng: 'SearXNG',
  system: 'System',
  monitoring: 'Monitoring',
  'fastapi-monitoring': 'FastAPI',
  'copilot-metrics': 'Copilot metrics',
  'supabase-monitor': 'Supabase',
  'verified-domains': 'Sending domains',
  audit: 'Audit log',
  operations: 'Operations Center',
  'voice-infrastructure': 'Voice Infrastructure',
  'platform-operations': 'Platform Operations',
  'jarvis-tools': 'Jarvis tools',
  'latency-profiler': 'Latency Profiler',
};

function segmentLabel(segment: string): string {
  if (LABELS[segment]) return LABELS[segment];
  if (/^[0-9a-f-]{36}$/i.test(segment)) return 'Detail';
  return segment.replace(/-/g, ' ');
}

export const EnterpriseBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;
  if (pathname === '/admin/login' || !pathname.startsWith(`${ADMIN_BASE}/`)) return null;

  const rest = pathname.slice(ADMIN_BASE.length).replace(/^\//, '');
  const segments = rest ? rest.split('/').filter(Boolean) : [];

  const crumbs: { to: string; label: string }[] = [
    { to: `${ADMIN_BASE}/dashboard`, label: 'Dashboard' },
  ];

  let acc = ADMIN_BASE;
  for (const seg of segments) {
    acc = `${acc}/${seg}`;
    crumbs.push({ to: acc, label: segmentLabel(seg) });
  }

  if (crumbs.length <= 1) return null;

  return (
    <nav aria-label="Breadcrumb" className="mb-4 flex flex-wrap items-center gap-1 text-xs text-enterprise-500">
      <Link
        to={`${ADMIN_BASE}/dashboard`}
        className="inline-flex items-center gap-0.5 font-medium text-enterprise-600 hover:text-emerald-700"
      >
        <Home className="h-3.5 w-3.5" />
        <span className="sr-only sm:not-sr-only">Home</span>
      </Link>
      {crumbs.slice(1).map((c, i) => {
        const last = i === crumbs.length - 2;
        return (
          <React.Fragment key={c.to}>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-enterprise-300" aria-hidden />
            {last ? (
              <span className="font-semibold text-enterprise-800">{c.label}</span>
            ) : (
              <Link to={c.to} className="font-medium text-enterprise-600 hover:text-emerald-700">
                {c.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};
